<template>
  <v-card>
    <v-card-title class="primary white--text">
      <v-row>
        <v-col class="headline">
          {{ $t('importDefects') }}
        </v-col>

        <v-col cols="auto">
          <BaseDialogActions hideFullscreen hideRefresh />
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="pt-5">
      <v-row>
        <v-col cols="12">
          <v-file-input v-model="file" accept=".csv" >
            <template #prepend-inner>
              <Icon
              icon="mdi-information-outline"
              :tooltipText="$t('downloadExampleTemplate')"
              @icon-clicked="exampleTemplateClicked"
              :small="false"/>
            </template>
          </v-file-input>
        </v-col>

        <v-col cols="12" v-if="previewVisible">
          <v-data-table
            dense
            :items="this.parsedData.data"
            :headers="this.headers"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.defectLbs="{ item }">
              {{ formatTons(parseInt(item.defectLbs) / 2000) }}
            </template>

            <template #item.defaultPercentage="{ item }">
              {{ `${parseInt(item.defaultPercentage)}%` }}
            </template>

            <template #item.category="{ item }">
              {{ DefectCategory.fromInt(item.category) }}
            </template>

            <template #item.cut="{ item }">
              <v-chip v-for="chip in deductionChipsFor(item)" :key="chip.id" small style="margin-top: 2px; margin-bottom: 2px; margin-right: 4px;">
                <Icon :icon="chip.icon" iconColor="grey darken-2"></Icon>
                <span>
                  {{ chip.text }}
                </span>
              </v-chip>
            </template>
          </v-data-table>
        </v-col>

        <v-col cols="12" v-if="errorsVisible">
          <v-row dense>
            <v-col cols="12">
              <span class="subtitle-1">{{ $t('oneOrMoreErrorsExistWithThisFile') }}</span>
            </v-col>

            <v-col cols="12" v-for="(error, i) in parsedData.errors" :key="`error-${i}`">
              <span>
                {{ error.message }}
              </span>
            </v-col>

            <v-col cols="12" v-for="(line, i) in Object.keys(parsedData.lineErrors)" :key="`line-${i}`">
              <v-row no-gutters>
                <v-col>
                  <span>
                    {{ $t('lineN', { n: line }) }}
                  </span>
                </v-col>

                <v-col cols="12" v-for="(lineError, j) in parsedData.lineErrors[line]" :key="`line-error-${j}`" class="pl-4">
                  <span>
                    {{ lineError.message }}
                  </span>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" v-for="(dupe, i) in duplicateNames" :key="`dupe-${i}`">
              <span>
                {{ $t('defectWithNameAlreadyExists', { name: dupe, location: location.name }) }}
              </span>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="px-0 py-4">
      <v-row justify="end">
        <v-col cols="auto">
          <v-btn
            @click="confirmImport"
            color="primary"
            :disabled="!enableSubmit"
          >
            {{ $t('import') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { parseCsv, CsvColumn, CsvTypeTransformation } from '@/utils/csv/parse.js'
import { DefectCategory } from '@/utils/Enumerations'
import { defectHeaders } from '@/headers/Defect.js'
import { mapActions } from 'vuex'
import { saveAs } from 'file-saver'
import { UPPER_DEFECT_WEIGHT_BOUND } from '@/utils/rules'
import { numberWithCommas } from '@/utils/NumericMutations'

export default {
  name: 'ImportDefects',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    location: {
      type: Object,
      required: true
    },
    defects: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    DefectCategory,
    file: null,
    parsedData: null,
    duplicateNames: []
  }),

  computed: {
    headers () {
      const headers = defectHeaders().filter(h => !['isActive', 'actions'].includes(h.value))
      headers.find(h => h.value === 'defectLBS').value = 'defectLbs'
      return headers
    },

    previewVisible () {
      return this.parsedData && this.parsedData.errors.length === 0 &&
        Object.values(this.parsedData.lineErrors).length === 0 &&
        this.duplicateNames.length === 0
    },

    errorsVisible () {
      return this.parsedData && (this.parsedData.errors.length > 0 ||
        Object.values(this.parsedData.lineErrors).length > 0 ||
        this.duplicateNames.length > 0)
    },

    enableSubmit () {
      return this.previewVisible
    }
  },

  watch: {
    async file (f) {
      if (!f) {
        this.parsedData = null
        this.duplicateNames = []
        return
      }
      const fileContent = await this.readFileAsString(this.file)
      const parsed = parseCsv(fileContent, this.getCsvSchema())
      this.parsedData = parsed
      this.duplicateNames = this.parsedData?.data ? this.parsedData.data
        .filter(d => this.defects.some(existingDefect => existingDefect.name.toLowerCase() === d.name.toLowerCase()))
        .map(d => d.name)
        : []
    }
  },

  methods: {
    ...mapActions('defects', ['createLocationDefect']),

    formatTons  (t) { return numberWithCommas(t, 3) },

    async readFileAsString (file) {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('error', reject)
        reader.addEventListener('load', () => { resolve(reader.result) })
        reader.readAsText(file)
      })
    },

    getCsvSchema () {
      return {
        name: CsvColumn.build(() => ({
          name: this.$t('name'),
          type: CsvTypeTransformation.from({
            validate: this.validateName
          })
        })),

        category: CsvColumn.build(types => ({
          name: this.$t('category'),
          type: types.enumNameToInt(DefectCategory, this.$t('category'))
        })),

        defectLbs: CsvColumn.from({
          name: this.$t('defectPounds'),
          type: CsvTypeTransformation.from({
            validate: this.validateDefectPounds
          })
        }),

        defaultPercentage: CsvColumn.from({
          name: this.$t('defaultPercentage'),
          type: CsvTypeTransformation.from({
            validate: this.validateDefaultPercentage
          })
        }),

        lengthDeduction: CsvColumn.from({
          name: this.$t('lengthDeduction'),
          type: CsvTypeTransformation.from({
            transform: v => v === this.$t('deductLength')
          })
        }),

        diameterDeduction: CsvColumn.from({
          name: this.$t('diameterDeduction'),
          type: CsvTypeTransformation.from({
            transform: v => v === this.$t('deductDiameter')
          })
        }),

        volumeDeduction: CsvColumn.from({
          name: this.$t('volumeDeduction'),
          type: CsvTypeTransformation.from({
            transform: v => v === this.$t('deductVolume')
          })
        })
      }
    },

    validateName (name) {
      return (name.length > 0 && name.length <= 64) || this.$t('invalidName')
    },

    validateDefectPounds (defectPounds) {
      return (defectPounds >= 0 && defectPounds <= UPPER_DEFECT_WEIGHT_BOUND) || this.$t('weightNotValidPounds')
    },

    validateDefaultPercentage (defaultPercentage) {
      return (defaultPercentage >= 0 && defaultPercentage <= 100) || this.$t('invalidPercentage')
    },

    async confirmImport () {
      if (!this.enableSubmit) {
        return
      }

      const response = await Promise.all(this.parsedData.data.map(d => {
        const request = {
          locationId: this.location.locationId,
          name: d.name,
          category: d.category,
          defectLbs: parseInt(d.defectLbs),
          defaultPercentage: parseInt(d.defaultPercentage),
          lengthDeduction: d.lengthDeduction,
          diameterDeduction: d.diameterDeduction,
          volumeDeduction: d.volumeDeduction,
          isActive: true
        }
        return this.createLocationDefect(request)
      }))

      this.$emit('import-complete', response)
    },

    exampleTemplateClicked () {
      saveAs(
        `data:text/csv;charset=utf-8,"Name","Category","Defect LBS","Default Percentage","${this.$t('lengthDeduction')}","${this.$t('diameterDeduction')}","${this.$t('volumeDeduction')}"\n"Bow","Mismanufactured","650","60","","",""\n"Split","Nature","700","85","${this.$t('deductLength')}","",""`,
        'ExampleLocationDefectImport.csv')
    },

    deductionChipsFor (defect) {
      const chips = []
      if (defect.lengthDeduction) chips.push({ id: 'lengthCut', icon: 'mdi-ruler', text: this.$t('lengthCut') })
      if (defect.diameterDeduction) chips.push({ id: 'diameterCut', icon: 'mdi-diameter', text: this.$t('diameterCut') })
      if (defect.volumeDeduction) chips.push({ id: 'volumeCut', icon: 'mdi-cube', text: this.$t('volumeCut') })
      return chips
    }
  }
}
</script>
